import React, { useCallback, useEffect } from 'react';

import { App, Button, Form, Input, Modal, Switch, Typography } from 'antd';

import { Wysiwyg } from 'v2/components/Shared/Wysiwyg';
import { useCreateRecoveryMessage } from 'v2/hooks/recoveryMessages/useCreateRecoveryMessage';
import { useUpdateRecoveryMessage } from 'v2/hooks/recoveryMessages/useUpdateRecoveryMessage';

export interface IRecoveryMessage {
  active: boolean;
  days: string;
  description: string;
  id?: number;
  text: string;
  title: string;
}

interface IProps {
  open: boolean;
  handleClose: () => void;
  recoveryEmail: any;
  editing: boolean;
  idProduct: number;
  registeredMessages: IRecoveryMessage[];
}

const DEFAULT_RECOVERY_MESSAGE: IRecoveryMessage = {
  active: true,
  days: '1',
  description: '',
  text: '',
  title: '',
  id: null
};

const EMPTY_HTML_PATTERNS = ['<p></p>', '<p><br></p>'];

const AVAILABLE_VARIABLES = [
  { key: '[[PRIMEIRONOME]]', description: 'Primeiro nome do cliente' },
  { key: '[[NOMEDOPRODUTOR]]', description: 'Nome e sobrenome do produtor' },
  { key: '[[NOMECONTEUDO]]', description: 'Nome do conteúdo em recuperação' },
  { key: '[[DTFATURA]]', description: 'Data de abertura da fatura / Ínicio do processo de compra' },
  { key: '[[DTFATVENC]]', description: 'Data de vencimento da fatura' },
  { key: '[[FATCOD]]', description: 'Código da fatura gerada na Eduzz' },
  { key: '[[CNTCOD]]', description: 'Código do seu produto' },
  {
    key: '[[CHAVEGW]]',
    description: 'Usado para geração do link de boleto, ex: https://sun.eduzz.com/thankyou/[[CNTCOD]]/[[CHAVEGW]]'
  }
];

const RecoveryMessageForm = ({ open, handleClose, recoveryEmail, editing, idProduct, registeredMessages }: IProps) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<IRecoveryMessage>();

  const updateMutation = useUpdateRecoveryMessage();
  const createMutation = useCreateRecoveryMessage();
  const submitting = updateMutation.isPending || createMutation.isPending;

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue(editing && recoveryEmail ? { ...recoveryEmail } : { ...DEFAULT_RECOVERY_MESSAGE });
  }, [open, editing, recoveryEmail, form]);

  const handleSubmit = useCallback(
    async (values: IRecoveryMessage) => {
      const payload: IRecoveryMessage = {
        ...values,
        days: String(values.days ?? ''),
        id: editing ? (recoveryEmail?.id ?? values.id) : null
      };

      try {
        if (editing) {
          await updateMutation.mutateAsync({
            productId: idProduct,
            messageId: payload.id,
            data: payload
          });
          message.success('Mensagem alterada com sucesso!');
        } else {
          await createMutation.mutateAsync({
            productId: idProduct,
            data: payload
          });
          message.success('Mensagem criada com sucesso!');
        }

        handleClose();
      } catch {
        message.error('Erro ao salvar mensagem de recuperação');
      }
    },
    [editing, recoveryEmail, updateMutation, createMutation, idProduct, handleClose, message]
  );

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={editing ? 'Editar mensagem' : 'Nova mensagem'}
      width={640}
      destroyOnHidden
      footer={
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <Button id='recovery-message-close' type='text' disabled={submitting} onClick={handleClose}>
            Fechar
          </Button>
          <Button id='recovery-message-save' type='primary' loading={submitting} onClick={() => form.submit()}>
            Salvar
          </Button>
        </div>
      }
    >
      <Typography.Paragraph type='secondary'>Crie ou edite sua mensagem de recuperação de venda</Typography.Paragraph>

      <Form form={form} layout='vertical' onFinish={handleSubmit} initialValues={DEFAULT_RECOVERY_MESSAGE}>
        <div className='mb-4 flex items-center gap-2'>
          <Form.Item name='active' valuePropName='checked' noStyle>
            <Switch id='recovery-message-active' />
          </Form.Item>
          <Typography.Text>Ativo</Typography.Text>
        </div>

        <div className='grid grid-cols-1 gap-x-4 md:grid-cols-12'>
          <Form.Item
            className='md:col-span-10'
            name='description'
            label='Descrição'
            rules={[{ required: true, message: 'A descrição é obrigatória' }]}
          >
            <Input id='recovery-message-description' />
          </Form.Item>

          <Form.Item
            className='md:col-span-2'
            name='days'
            label='Dias'
            rules={[
              { required: true, message: 'Dias é obrigatório' },
              {
                validator: async (_, value) => {
                  if (value === '' || value == null) {
                    return;
                  }

                  if (Number.parseInt(String(value), 10) < 0) {
                    throw new Error('Dias não pode ser menor que 0');
                  }

                  const messageId = form.getFieldValue('id');
                  const hasMessageForThatDay = registeredMessages.some(
                    (registeredMessage: IRecoveryMessage) =>
                      String(registeredMessage.days) === String(value) &&
                      registeredMessage.active &&
                      registeredMessage.id !== messageId
                  );

                  if (hasMessageForThatDay) {
                    throw new Error('Já existe uma mensagem cadastrada para esse dia');
                  }
                }
              }
            ]}
          >
            <Input id='recovery-message-days' inputMode='numeric' />
          </Form.Item>
        </div>

        <Form.Item name='title' label='Título do E-mail'>
          <Input id='recovery-message-title' />
        </Form.Item>

        <Form.Item
          name='text'
          label='Mensagem do E-mail'
          rules={[
            {
              validator: async (_, value) => {
                if (!value?.trim() || EMPTY_HTML_PATTERNS.includes(value)) {
                  throw new Error('Preencha a mensagem para e-mail');
                }
              }
            }
          ]}
        >
          <Wysiwyg value='' onChange={() => undefined} />
        </Form.Item>

        <Form.Item name='id' hidden>
          <Input />
        </Form.Item>
      </Form>

      <div>
        <Typography.Title level={5}>Variáveis disponíveis</Typography.Title>
        <div className='space-y-1'>
          {AVAILABLE_VARIABLES.map(variable => (
            <div key={variable.key}>
              <Typography.Text strong>{variable.key}</Typography.Text>
              <Typography.Text> - {variable.description}</Typography.Text>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default RecoveryMessageForm;
